import styled from 'styled-components'
import { omniaTheme } from '../../theme/omniaTheme'

export const CoverContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 10mm 10mm 0 10mm;
  box-sizing: border-box;
  /* The cover is the only section with a gray page background — the rest
     of the report inverts to white-page / gray-cards. */
  background: ${omniaTheme.colors.coverPageBg};

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 10mm 10mm 0 10mm !important;
    background: ${omniaTheme.colors.coverPageBg} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Top header strip
// ============================================================

export const TopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: ${omniaTheme.fonts.label};
  font-size: 9.5px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.text};
  text-transform: uppercase;

  @media print {
    display: flex !important;
    justify-content: space-between !important;
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 9.5px !important;
    color: ${omniaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const HeaderDivider = styled.hr`
  width: 100%;
  height: 0;
  border: 0;
  border-top: 0.5px solid ${omniaTheme.colors.borderStrong};
  margin: 6mm 0 8mm 0;

  @media print {
    border-top: 0.5px solid ${omniaTheme.colors.borderStrong} !important;
    margin: 6mm 0 8mm 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Display headline
// ============================================================

export const DisplayHeadline = styled.h1`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 60px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 60px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.black} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PreHeroDivider = styled.hr`
  width: 100%;
  height: 0;
  border: 0;
  border-top: 0.5px solid ${omniaTheme.colors.strokeLine};
  margin: 12mm 0 4mm 0;

  @media print {
    border-top: 0.5px solid ${omniaTheme.colors.strokeLine} !important;
    margin: 12mm 0 4mm 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Hero photo
// ============================================================

export const HeroPhotoWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 515 / 400;
  background: ${omniaTheme.colors.borderStrong};
  overflow: hidden;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};

  @media print {
    width: 100% !important;
    aspect-ratio: 515 / 400 !important;
    background: ${omniaTheme.colors.borderStrong} !important;
    cursor: default !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    /* No photo → print blank (no grey box) instead of a placeholder. */
    &:not(:has(img)) {
      background: transparent !important;
    }
  }
`

export const HeroPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  @media print {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
  }
`

export const HeroPhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.textMuted};

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: ${omniaTheme.colors.textMuted} !important;
  }
`

export const PhotoEditOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(47, 16, 115, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  font-family: ${omniaTheme.fonts.label};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.white};
  text-transform: uppercase;

  ${HeroPhotoWrapper}:hover & {
    opacity: 1;
  }

  @media print {
    display: none !important;
  }
`

// ============================================================
// Property meta
// ============================================================

export const PropertyMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 8mm 0 6mm 0;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important;
    margin: 8mm 0 6mm 0 !important;
  }
`

export const PropertyAddressLabel = styled.span`
  font-family: ${omniaTheme.fonts.label};
  font-size: 10px;
  font-weight: ${omniaTheme.fontWeights.light};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  color: ${omniaTheme.colors.text};
  text-transform: uppercase;

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 10px !important;
    color: ${omniaTheme.colors.text} !important;
    text-transform: uppercase !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PropertyTypeHeading = styled.h2`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 32px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.1;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 32px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.black} !important;
    line-height: 1.1 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Metric cards
// ============================================================

export const MetricCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6mm;
  margin-top: 4mm;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr 1fr !important;
    gap: 6mm !important;
    margin-top: 4mm !important;
  }
`

export const MetricCard = styled.div`
  /* Cover stays gray-page / white-cards regardless of the theme default
     (other sections use white-page / gray-cards). */
  background: ${omniaTheme.colors.coverCardBg};
  padding: 24px 24px 24px 24px;
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 132px;

  @media print {
    background: ${omniaTheme.colors.coverCardBg} !important;
    padding: 24px 24px 24px 24px !important;
    border: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
    min-height: 132px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const MetricValue = styled.div`
  font-family: ${omniaTheme.fonts.heading};
  font-size: 28px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-size: 28px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.black} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const MetricLabel = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 13px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 13px !important;
    color: ${omniaTheme.colors.text} !important;
  }
`

/**
 * Hairline beneath the metric value+label, ~24px below them.
 * Sits inside the card with breathing room above and below — does NOT push to the bottom.
 */
export const MetricCardDivider = styled.hr`
  width: 100%;
  height: 0;
  border: 0;
  border-top: 0.5px solid ${omniaTheme.colors.border};
  margin: 24px 0 0 0;

  @media print {
    border-top: 0.5px solid ${omniaTheme.colors.border} !important;
    margin: 24px 0 0 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Bottom row: broker + logo
// ============================================================

export const BottomRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 14mm;
  padding-bottom: 14mm;

  @media print {
    display: flex !important;
    align-items: flex-end !important;
    justify-content: space-between !important;
    margin-top: auto !important;
    padding-top: 14mm !important;
    padding-bottom: 14mm !important;
  }
`

export const BrokerBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media print {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
  }
`

export const BrokerPhoto = styled.div<{ $imageUrl?: string }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ $imageUrl }) =>
    $imageUrl ? `url('${$imageUrl}') center/cover` : '#E5E5E5'};

  @media print {
    width: 50px !important;
    height: 50px !important;
    border-radius: 50% !important;
    background: ${({ $imageUrl }) =>
      $imageUrl
        ? `url('${$imageUrl}') center/cover !important`
        : '#E5E5E5 !important'};
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const BrokerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 2px !important;
  }
`

export const BrokerName = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 14px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.black};

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 14px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.black} !important;
  }
`

export const BrokerRole = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 12px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.textMuted};

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 12px !important;
    color: ${omniaTheme.colors.textMuted} !important;
  }
`

export const LogoSlot = styled.div`
  /* Logo SVG sizes itself; container just aligns */
  display: flex;
  align-items: flex-end;

  @media print {
    display: flex !important;
    align-items: flex-end !important;
  }
`
